import { useState } from 'react';
import {
  Box,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Tooltip,
  Popover,
  Slider,
  Typography,
} from '@mui/material';
import {
  Create as PenIcon,
  Brush as HighlighterIcon,
  TextFields as TextIcon,
  PanTool as SelectIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  LayersClear as ClearPageIcon,
  Delete as DeleteIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Palette as PaletteIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import type { ToolType } from './NotebookCanvas';

type NotebookToolbarProps = {
  tool: ToolType;
  onToolChange: (tool: ToolType) => void;
  penColor: string;
  onColorChange: (color: string) => void;
  penWidth: number;
  onWidthChange: (width: number) => void;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onUndo: () => void;
  onRedo: () => void;
  onClear: () => void;
  onDeleteSelected?: () => void;
  hasSelection?: boolean;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  onInsertImage?: () => void;
  readOnly?: boolean;
  compact?: boolean;
};

const PRESET_COLORS = [
  '#000000', // Black
  '#333333', // Dark Gray
  '#666666', // Gray
  '#0f2ccf', // Primary Blue
  '#2f5dff', // Secondary Blue
  '#5a48f5', // Purple
  '#df3336', // Red
  '#ff6b6b', // Light Red
  '#4b916d', // Green
  '#ffa500', // Orange
  '#ffd700', // Yellow
];

export const NotebookToolbar = ({
  tool,
  onToolChange,
  penColor,
  onColorChange,
  penWidth,
  onWidthChange,
  zoom,
  onZoomChange,
  onUndo,
  onRedo,
  onClear,
  onDeleteSelected,
  hasSelection = false,
  isFullscreen = false,
  onToggleFullscreen,
  onInsertImage,
  readOnly = false,
  compact = false,
}: NotebookToolbarProps) => {
  const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null);

  const handleToolChange = (_: React.MouseEvent<HTMLElement>, newTool: ToolType | null) => {
    if (newTool) {
      onToolChange(newTool);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        backgroundColor: 'background.paper',
        borderRadius: 1,
        boxShadow: 1,
        flexWrap: 'wrap',
      }}
    >
      {/* Drawing Tools */}
      {!readOnly && (
        <>
          <ToggleButtonGroup
            value={tool}
            exclusive
            onChange={handleToolChange}
            size="small"
            aria-label="drawing tools"
          >
            <ToggleButton value="select" aria-label="select">
              <Tooltip title="Select">
                <SelectIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="pen" aria-label="pen">
              <Tooltip title="Pen">
                <PenIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="highlighter" aria-label="highlighter">
              <Tooltip title="Highlighter">
                <HighlighterIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="text" aria-label="text">
              <Tooltip title="Text">
                <TextIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Insert Image */}
          {onInsertImage && (
            <Tooltip title="Insert Image">
              <IconButton onClick={onInsertImage} size="small">
                <ImageIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Divider orientation="vertical" flexItem />

          {/* Color Picker */}
          <Tooltip title="Color">
            <IconButton
              onClick={(e) => setColorAnchor(e.currentTarget)}
              size="small"
              sx={{
                backgroundColor: penColor,
                width: 28,
                height: 28,
                border: '2px solid',
                borderColor: 'divider',
                '&:hover': {
                  backgroundColor: penColor,
                  opacity: 0.8,
                },
              }}
            >
              <PaletteIcon sx={{ color: 'transparent' }} fontSize="small" />
            </IconButton>
          </Tooltip>

          <Popover
            open={Boolean(colorAnchor)}
            anchorEl={colorAnchor}
            onClose={() => setColorAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Colors
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 0.5,
                  mt: 1,
                }}
              >
                {PRESET_COLORS.map((color) => (
                  <IconButton
                    key={color}
                    onClick={() => {
                      onColorChange(color);
                      setColorAnchor(null);
                    }}
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: color,
                      border: color === penColor ? '3px solid' : '1px solid',
                      borderColor: color === penColor ? 'primary.main' : 'divider',
                      '&:hover': {
                        backgroundColor: color,
                        opacity: 0.8,
                      },
                    }}
                  />
                ))}
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Pen Size: {penWidth}px
                </Typography>
                <Slider
                  value={penWidth}
                  onChange={(_, value) => onWidthChange(value as number)}
                  min={1}
                  max={10}
                  step={1}
                  size="small"
                />
              </Box>
            </Box>
          </Popover>

          <Divider orientation="vertical" flexItem />

          {/* Undo/Redo */}
          <Tooltip title="Undo">
            <IconButton onClick={onUndo} size="small">
              <UndoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Redo">
            <IconButton onClick={onRedo} size="small">
              <RedoIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem />

          {/* Delete Selected (only when selection exists) */}
          <Tooltip title="Delete Selected">
            <span>
              <IconButton
                onClick={onDeleteSelected}
                size="small"
                color="error"
                disabled={!hasSelection}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Clear Page">
            <IconButton onClick={onClear} size="small" color="warning">
              <ClearPageIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem />
        </>
      )}

      {/* Zoom Controls - hide in compact/fullscreen mode */}
      {!compact && (
        <>
          <Tooltip title="Zoom Out">
            <IconButton
              onClick={() => onZoomChange(Math.max(0.25, zoom - 0.25))}
              size="small"
              disabled={zoom <= 0.25}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="body2" sx={{ minWidth: 45, textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </Typography>
          <Tooltip title="Zoom In">
            <IconButton
              onClick={() => onZoomChange(Math.min(2, zoom + 0.25))}
              size="small"
              disabled={zoom >= 2}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem />
        </>
      )}

      {/* Fullscreen Toggle */}
      {onToggleFullscreen && (
        <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen Mode'}>
          <IconButton onClick={onToggleFullscreen} size="small">
            {isFullscreen ? (
              <FullscreenExitIcon fontSize="small" />
            ) : (
              <FullscreenIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
